/**
 * Storage service
 */
export class StorageService {
  /**
   * Persists a value in the local storage for the given key.
   *
   * @param key   Key
   * @param value Value
   */
  persistValue(key: string, value: string) {
    localStorage.setItem(key, value);
  }

  /**
   * Gets a persisted value from the local storage for the given key.
   *
   * @param key Key
   */
  getPersistedValue(key: string): string | null {
    return localStorage.getItem(key);
  }

  /**
   * Removes a key and its value from the local storage.
   *
   * @param key Key
   */
  removePersistedValue(key: string) {
    localStorage.removeItem(key);
  }

  /**
   * Gets a persisted object from the local storage for the given key.
   *
   * @param key Key
   */
  private getPersistedObject(key: string): object | undefined {
    const object = localStorage.getItem(key);
    return object ? JSON.parse(object) : undefined;
  }

  /**
   * Persists a sub object in the local storage with the given parent key/key combination.
   *
   * @param parentKey Parent key
   * @param key       Key
   * @param object    Object
   */
  persistSubObject(parentKey: string, key: string, object: object) {
    const persistedObject = this.getPersistedObject(parentKey);
    localStorage.setItem(parentKey, JSON.stringify({
      ...persistedObject,
      [key]: object,
    }));
  }

  /**
   * Gets a persisted sub object for the given parent key/key combination.
   *
   * @param parentKey Parent key
   * @param key       Key
   */
  getPersistedSubObject<T extends object>(parentKey: string, key: string): T | undefined {
    const object = localStorage.getItem(parentKey);
    return this.getObject(key, object)
  }

  /**
   * Saves the value to the session storage with the given key.
   *
   * @param key     Key
   * @param object  Object
   */
  saveSessionObject(key: string, object: object) {
    sessionStorage.setItem(key, JSON.stringify(object));
  }

  /**
   * Gets the object from the session storage for the given key.
   *
   * @param key Key
   */
  getSessionObject<T extends object>(key: string): T | undefined {
    const object = sessionStorage.getItem(key);
    return object ? JSON.parse(object) : undefined;
  }

  /**
   * Clears all keys from the session storage.
   */
  clearSession(): void {
    sessionStorage.clear()
  }

  /**
   * Gets the object as JSON if a value exists.
   *
   * @param key     Key
   * @param object  Object
   */
  private getObject(key: string, object: string | null) {
    return object ? JSON.parse(object)[key] : undefined;
  }
}

/**
 * Storage service const
 */
export const storageService = new StorageService();

/**
 * Creates a hash key for the given key. This key can be used as key for storing information, e.g. in local storage.
 *
 * @param key Key
 */
export function hashKey(key: string): string {
  if (key.length === 0) {
    return '';
  }

  let hashedKey = 0;
  for (let i = 0; i < key.length; i++) {
    const charCode = key.charCodeAt(i);
    hashedKey = (hashedKey << 3) + charCode;
    hashedKey = hashedKey & hashedKey;
  }

  return hashedKey.toString();
}
